/* global google */
import React, { useState, useEffect, useContext, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";

import MDSnackbar from "components/MDSnackbar";
import MDBox from "components/MDBox";
import TypeBadge from "components/ReportUI/Typebadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AccountsContext } from "context/AccountsContext";
import { routeDeviationReportendpoint } from "config";
import RouteDeviationTable from "./routeDeviationReportTable";
import SingleDatePicker from "layouts/SingleDatePicker/singleDatePicker";
import DhundhooLoader from "../Loader/Loader";

// Custom Standalone Polyline Decoder
const decodePolylineString = (encodedStr) => {
    if (!encodedStr) return [];
    let len = encodedStr.length;
    let index = 0;
    let array = [];
    let lat = 0;
    let lng = 0;

    while (index < len) {
        let b;
        let shift = 0;
        let result = 0;
        do {
            b = encodedStr.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += dlat;

        shift = 0;
        result = 0;
        do {
            b = encodedStr.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += dlng;

        array.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return array;
};

function RouteDeviationReport() {
    const [reportData, setReportData] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [vehicleIds, setVehicleIds] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal popup state handlers
    const [openMapDialog, setOpenMapDialog] = useState(false);
    const [selectedJourneyInfo, setSelectedJourneyInfo] = useState(null);

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const { fetchThings, things } = useContext(AccountsContext);

    useEffect(() => {
        fetchThings();
    }, []);

    // Map Plotter
    const renderGoogleMapDirectly = (journeyData) => {
        if (!journeyData) return;

        setTimeout(() => {
            const targetElement = mapRef.current || document.getElementById("route-deviation-map-canvas");

            if (!targetElement) {
                console.error("Map rendering canvas not available inside DOM structure.");
                return;
            }

            try {
                if (window.google && window.google.maps) {

                    let decodedWaypoint = decodePolylineString(journeyData.waypointPolyline || "");
                    let decodedPathTaken = decodePolylineString(journeyData.pathTakenPolyline || "");

                    const bounds = new google.maps.LatLngBounds();
                    let mapCenter = { lat: 12.9716, lng: 77.5946 };

                    if (decodedWaypoint.length > 0) {
                        mapCenter = decodedWaypoint[Math.round(decodedWaypoint.length / 2)];
                    } else if (decodedPathTaken.length > 0) {
                        mapCenter = decodedPathTaken[Math.round(decodedPathTaken.length / 2)];
                    }

                    const map = new google.maps.Map(targetElement, {
                        center: mapCenter,
                        zoom: 13,
                        streetViewControl: false,
                        scaleControl: true,
                        mapTypeControl: false,
                        fullscreenControl: false,
                    });

                    mapInstance.current = map;

                    // ✅ FIXED: Planned Route Line Plotting (Black color with full visibility opacity)
                    if (decodedWaypoint.length > 0) {
                        decodedWaypoint.forEach((point) => bounds.extend(point));
                        new google.maps.Polyline({
                            path: decodedWaypoint,
                            geodesic: true,
                            strokeColor: "#000000", // Dynamic pure solid black line
                            strokeOpacity: 1.0,
                            strokeWeight: 5,
                            map: map
                        });
                    }

                    // Actual Route Line Plotting (Green color)
                    if (decodedPathTaken.length > 0) {
                        decodedPathTaken.forEach((point) => bounds.extend(point));
                        new google.maps.Polyline({
                            path: decodedPathTaken,
                            geodesic: true,
                            strokeColor: "#10AC84",
                            strokeOpacity: 1.0,
                            strokeWeight: 5,
                            map: map
                        });
                    }

                    // Auto Fit Camera Bounds (Markers logic has been fully removed from here)
                    if (decodedWaypoint.length > 0 || decodedPathTaken.length > 0) {
                        map.fitBounds(bounds);
                    }

                    google.maps.event.trigger(map, "resize");

                } else {
                    setErrorMessage("Google Maps global configurations are missing.");
                    openErrorSB();
                }
            } catch (err) {
                console.error("Critical rendering breakdown: ", err);
                setErrorMessage("An unexpected layout problem crashed maps initialization threads.");
                openErrorSB();
            }
        }, 400);
    };

    const getTodayDateString = () => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const defaultDate = getTodayDateString();
    const [selectedDate, setSelectedDate] = useState(defaultDate);

    const openSuccessSB = () => setSuccessSB(true);
    const closeSuccessSB = () => setSuccessSB(false);
    const openErrorSB = () => setErrorSB(true);
    const closeErrorSB = () => setErrorSB(false);

    // Table Columns Setup
    const columns = [
        { Header: "Date", accessor: "date", width: "10%" },
        { Header: "Vehicle Number", accessor: "thingId", width: "15%" },
        { Header: "Journey Name", accessor: "journeyName", width: "20%" },
        {
            Header: "Journey Type", accessor: "journeyType", width: "15%",
            Cell: ({ value }) => (
                <TypeBadge
                    value={value}
                    config={{}}
                    defaultConfig={{ color: "#1E40AF", bg: "#E9F1FF", icon: null }}
                />
            ),
        },
        { Header: "Journey Time", accessor: "journeyTime", width: "15%" },
        {
            Header: "Route Deviation",
            accessor: "routeDeviation",
            width: "15%",
            Cell: ({ row }) => {
                const devVal = parseFloat(row.original.routeDeviation);
                const displayVal = !isNaN(devVal) ? `${devVal.toFixed(2)} %` : "0.00 %";
                return (
                    <MDTypography
                        variant="button"
                        fontWeight="medium"
                        onClick={() => {
                            setSelectedJourneyInfo(row.original);
                            setOpenMapDialog(true);
                            renderGoogleMapDirectly(row.original);
                        }}
                        sx={{
                            color: "#1A73E8",
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "#0d47a1" }
                        }}
                    >
                        {displayVal}
                    </MDTypography>
                );
            }
        },
    ];

    const handleCloseMap = () => {
        setOpenMapDialog(false);
        setSelectedJourneyInfo(null);
        mapInstance.current = null;
    };

    const fetchReport = async () => {
        if (!selectedDate) {
            setErrorMessage("Please select a date first");
            openErrorSB();
            return;
        }

        const token = localStorage.getItem("token");
        setLoading(true);
        setErrorMessage("");

        try {
            const data = {
                date: selectedDate,
                zone: "Asia/Kolkata",
                thingIds: vehicleIds,
            };

            const config = {
                method: "post",
                url: routeDeviationReportendpoint,
                headers: {
                    "x-dhundhoo-session": token,
                    "Content-Type": "application/json",
                },
                data,
            };

            const response = await axios.request(config);
            const rows = response.data.things || [];
            const formattedRows = rows.map((row) => ({
                date: row.dateRange,
                thingId: row.thingId,
                journeyName: row.journeyName,
                journeyType: row.journeyType,
                journeyTime: row.timings,
                routeDeviation: row.routeDeviation ?? 0,
                pathTakenPolyline: row.pathTakenPolyline || "",
                waypointPolyline: row.waypointPolyline || "",
            }));

            setReportData(formattedRows);
            openSuccessSB();
        } catch (error) {
            console.error("Fetch Report Error:", error);
            setErrorMessage("Error loading route deviation report");
            openErrorSB();
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = () => {
        if (reportData.length === 0) {
            setErrorMessage("No data to export");
            openErrorSB();
            return;
        }

        const formattedDate = selectedDate || "export";
        const headers = ["Date", "Vehicle Number", "Journey Name", "Journey Type", "Journey Time", "Route Deviation (%)"];

        const rows = reportData.map((row) => {
            const devVal = parseFloat(row.routeDeviation);
            const deviationString = !isNaN(devVal) ? `${devVal.toFixed(2)} %` : "0.00 %";
            return [
                `"${row.date || ''}"`,
                `"${row.thingId || ''}"`,
                `"${row.journeyName || ''}"`,
                `"${row.journeyType || ''}"`,
                `"${row.journeyTime || ''}"`,
                `"${deviationString}"`
            ].join(",");
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `RouteDeviationReport_${formattedDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearFilters = () => {
        setVehicleIds([]);
        setReportData([]);
        setErrorMessage("");
        setSelectedDate(defaultDate);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox p={3} pb={0}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Autocomplete
                                            multiple
                                            options={things || []}
                                            getOptionLabel={(option) => option.thingId || ""}
                                            filterOptions={(options, { inputValue }) => {
                                                const searchValue = inputValue.toLowerCase().trim();

                                                return options.filter((option) => {
                                                    const name = (option.name || "").toLowerCase();
                                                    const nickName = (option.nickName || "").toLowerCase();
                                                    const thingId = (option.thingId || "").toLowerCase();

                                                    return (
                                                        name.includes(searchValue) ||
                                                        nickName.includes(searchValue) ||
                                                        thingId.includes(searchValue)
                                                    );
                                                });
                                            }}
                                            value={things.filter((t) => vehicleIds.includes(t.thingId))}
                                            onChange={(event, newValue) => {
                                                setVehicleIds(newValue.map((v) => v.thingId));
                                            }}
                                            renderOption={(props, option) => (
                                                <li
                                                    {...props}
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        width: "100%",
                                                    }}
                                                >
                                                    <span>{option.nickName || option.name || "-"}</span>
                                                    <span style={{ color: "#666" }}>{option.thingId}</span>
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Vehicles"
                                                    fullWidth
                                                    size="small"
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#FB8C00",
                                                            },
                                                        },
                                                        "& .MuiInputLabel-root.Mui-focused": {
                                                            color: "#FB8C00",
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <div style={{ width: '70%' }}>
                                            <SingleDatePicker
                                                date={selectedDate}
                                                onChange={(newDate) => setSelectedDate(newDate)}
                                                disabled={loading}
                                            />
                                        </div>
                                    </Grid>

                                    <Grid item xs={4} sm={2} md={1}>
                                        <MDButton fullWidth variant="gradient" color="warning" size="small" onClick={fetchReport} disabled={loading}>
                                            {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                                        </MDButton>
                                    </Grid>

                                    <Grid item xs={4} sm={2} md={1}>
                                        <MDButton fullWidth variant="outlined" color="error" size="small" onClick={clearFilters} disabled={loading}>
                                            CLEAR
                                        </MDButton>
                                    </Grid>

                                    <Grid item xs={4} sm={2} md={1}>
                                        <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || reportData.length === 0} />
                                    </Grid>
                                </Grid>
                            </MDBox>

                            {errorMessage && (
                                <MDBox px={2} pt={2}>
                                    <Alert severity="error">{errorMessage}</Alert>
                                </MDBox>
                            )}

                            <MDBox pt={3} display="flex" justifyContent="center">
                                {loading ? (
                                    <DhundhooLoader />
                                ) : (
                                    <RouteDeviationTable
                                        table={{ columns, rows: reportData }}
                                        isSorted={true}
                                        entriesPerPage={true}
                                        showTotalEntries={true}
                                        noEndBorder
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Modal Container Design */}
            <Dialog
                open={openMapDialog}
                onClose={handleCloseMap}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* component="span" adding properties resolves the HTML h6 under h2 nesting warning */}
                    <MDTypography variant="h6" component="span" fontWeight="bold">
                        Route Deviation
                    </MDTypography>
                    <Icon onClick={handleCloseMap} sx={{ cursor: 'pointer', color: 'grey.500' }}>close</Icon>
                </DialogTitle>

                <DialogContent dividers sx={{ padding: 0, height: "500px", overflow: "hidden" }}>
                    <div
                        ref={mapRef}
                        id="route-deviation-map-canvas"
                        style={{
                            width: "100%",
                            height: "100%",
                            minHeight: "500px",
                            backgroundColor: "#e5e3df"
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <MDButton onClick={handleCloseMap} variant="outlined" color="info" size="small">
                        OK
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar color="success" icon="check" title="Route Deviation Report" content="Data Loaded Successfully" open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite />
            <MDSnackbar color="error" icon="warning" title="Route Deviation Report" content={errorMessage || "Failed to load report"} open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite />
        </DashboardLayout>
    );
}

export default RouteDeviationReport;